import React, { useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { searchSongsUnified } from '../helpers/searchSongs';
import AutoCompleteSearchInput from "./atoms/AutoCompleteSearchInput";
import {useAppSelector} from "../hooks/useAppSelector";
import {useI18n} from "../i18n";

interface Song {
    id?: number | string;
    uuid?: string;
    external_id?: string;
    title: string;
    artist: string;
    album?: string;
    song_art?: string;
    song_key?: string;
    file_url?: string;
    duration?: number;
    from_search?: boolean;
}

interface Props {
    onSelect: (song: Partial<Song>) => void;
    placeholder?: string;
    isStatic?: boolean;
    showPreview?: boolean;
    allowClear?: boolean;
}

const SongSearchInput: React.FC<Props> = ({
                                              onSelect,
                                              placeholder,
                                              isStatic = false,
                                              showPreview = true,
                                              allowClear = true,
                                          }) => {
    const {t} = useI18n();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Song[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<Song | null>(null);
    const config = useAppSelector(state => state.global.config);

    const debouncedSearch = useMemo(() =>
        debounce(async (text: string) => {
            if (text.length < 2) {
                setResults([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const songs = await searchSongsUnified(text, config?.search_source);
                setResults(songs);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }, 500), []);

    const handleSearch = (text: string) => {
        setQuery(text);
        debouncedSearch(text);
    };

    const handleSelect = (song: Song) => {
        setSelected(song);
        onSelect(song);
        setQuery('');
        setResults([]);
    };

    const handleRemove = (song: Song) => {
        if (selected?.uuid === song.uuid) {
            setSelected(null);
        }
        setResults(results.filter(s => s.uuid !== song.uuid));
    };

    return (
        <div className="space-y-2">
            <AutoCompleteSearchInput
                items={results}
                onChange={handleSelect}
                handleSearch={handleSearch}
                isStatic={isStatic}
                onRemove={allowClear ? handleRemove : undefined}
                value={selected}
                type="setlist_songs"
                loading={loading}
                query={query}
                setQuery={setQuery}
                placeholder={placeholder || t('components.songSearchInput.searchForSongs')}
            />

            {showPreview && selected && (
                <div className="text-sm text-white/70">
                    {t('components.songSearchInput.selectedSong', {title: selected.title, artist: selected.artist})}
                </div>
            )}
        </div>
    );
};

export default SongSearchInput;
